import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Translation resources
const resources = {
  en: {
    translation: {
      app: {
        title: 'Cannes Outfit Planner',
        subtitle: 'Plan your perfect event wardrobe',
      },
      auth: {
        login: 'Login',
        register: 'Register',
        email: 'Email',
        password: 'Password',
        name: 'Name',
        logout: 'Logout',
        noAccount: "Don't have an account?",
        hasAccount: 'Already have an account?',
        registerNow: 'Register now',
        loginNow: 'Login now',
        welcomeBack: 'Welcome back!',
        createAccount: 'Create your account',
      },
      navigation: {
        home: 'Home',
        myEvents: 'My Events',
        newEvent: 'New Event',
        settings: 'Settings',
        back: 'Back',
        next: 'Next',
        save: 'Save',
        cancel: 'Cancel',
        delete: 'Delete',
        edit: 'Edit',
      },
      outfit: {
        title: 'Outfit',
        day: 'Day',
        of: 'of',
        hair: 'Hair',
        jewelry: 'Jewelry',
        jewels: 'Jewels',
        purse: 'Purse',
        bag: 'Bag',
        shoes: 'Shoes',
        accessories: 'Accessories',
        notes: 'Notes',
        addPhotos: 'Add Photos',
        dragDrop: 'Drag & drop',
        or: 'or',
        clickToUpload: 'click to upload',
        maxPhotos: 'max {{max}} photos',
        autosave: 'Autosave',
      },
      template: {
        title: 'Templates',
        select: 'Select Template',
        '2piece': '2 Piece',
        dress: 'Dress',
        full: 'Full Look',
        custom: 'Custom',
      },
      event: {
        title: 'Events',
        create: 'Create Event',
        name: 'Event Name',
        startDate: 'Start Date',
        endDate: 'End Date',
        totalDays: 'Total Days',
        noEvents: 'No events yet',
        createFirst: 'Create your first event',
        deleteConfirm: 'Are you sure you want to delete this event?',
      },
      export: {
        title: 'Export',
        pdf: 'Export PDF',
        download: 'Download Plan',
        fashionPlan: 'FASHION PLAN',
        styleTitle: 'Style & Export',
      },
      common: {
        save: 'Save',
        cancel: 'Cancel',
        delete: 'Delete',
        confirm: 'Confirm',
        uploading: 'Uploading...',
        error: 'Error',
        photos: 'photos',
        hello: 'Hi',
        deleteEvent: 'Delete Event',
        example: 'e.g.',
        backToEvents: 'Back to events',
        previousDay: 'Previous day',
        nextDay: 'Next day',
        openStyleExport: 'Open style and export',
        editDays: 'Edit days',
        loadMore: 'Load more',
        sponsor: 'Sponsored by',
        sponsorDesc: 'Sicilian artisan jewelry',
        visitShop: 'Visit the shop',
        sponsorTagline: 'jewelry from the heart of Sicily',
      },
      upload: {
        invalidType: 'Only JPG, PNG, WebP and HEIC files are allowed',
        tooLarge: 'File size must be under 10 MB',
        failed_one: '{{count}} file failed to upload',
        failed_other: '{{count}} files failed to upload',
      },
      landing: {
        heroTitle: 'Your perfect outfit, day by day',
        heroSubtitle: 'Plan every detail of your wardrobe for Cannes — from the full look to accessories. Photos, notes, and PDFs ready to share.',
        cta: 'Get started — it\'s free',
        noCreditCard: 'No credit card required',
        featuresTag: 'Everything you need',
        featuresTitle: 'Every detail, under control',
        feat1Title: 'Organize by day',
        feat1Desc: 'Create your event and plan a complete outfit for each day. Navigate easily between days.',
        feat2Title: 'Photos for every category',
        feat2Desc: 'Upload photos for outfits, shoes, jewelry, bags and hair. Everything visually organized.',
        feat3Title: 'Ready-made templates',
        feat3Desc: 'Choose from preset templates: 2 Piece, Dress, Full Look. Or customize the categories.',
        feat4Title: 'Export to PDF',
        feat4Desc: 'Generate an elegant PDF with your complete plan. Each day on a separate page, ready to share.',
        feat5Title: 'Auto-save',
        feat5Desc: 'Every change is automatically saved to the cloud. You\'ll never lose your work.',
        feat6Title: 'Multilingual',
        feat6Desc: 'Available in English, Italian, French, Spanish and German. Perfect for international events.',
        howTag: 'Super simple',
        howTitle: 'How it works',
        step1Title: 'Create your event',
        step1Desc: 'Name your event and choose how many days it lasts.',
        step2Title: 'Plan every look',
        step2Desc: 'Upload photos, add notes, choose accessories for each day.',
        step3Title: 'Export and share',
        step3Desc: 'Download the PDF with all your outfits, ready to print or send.',
        sponsoredBy: 'Project sponsored by',
        ctaTitle: 'Ready for Cannes?',
        ctaDesc: 'Create your free account and start planning the perfect wardrobe for your event.',
        ctaButton: 'Create your account',
      },
      language: {
        title: 'Language',
        en: 'English',
        it: 'Italian',
        fr: 'French',
        es: 'Spanish',
        de: 'German',
      },
      errors: {
        required: 'This field is required',
        invalidEmail: 'Invalid email address',
        passwordShort: 'Password must be at least 6 characters',
        generic: 'Something went wrong',
        network: 'Network error',
      },
    },
  },
  it: {
    translation: {
      app: {
        title: 'Cannes Outfit Planner',
        subtitle: 'Pianifica il tuo guardaroba perfetto per l\'evento',
      },
      auth: {
        login: 'Accedi',
        register: 'Registrati',
        email: 'Email',
        password: 'Password',
        name: 'Nome',
        logout: 'Esci',
        noAccount: 'Non hai un account?',
        hasAccount: 'Hai già un account?',
        registerNow: 'Registrati ora',
        loginNow: 'Accedi ora',
        welcomeBack: 'Bentornata!',
        createAccount: 'Crea il tuo account',
      },
      navigation: {
        home: 'Home',
        myEvents: 'I Miei Eventi',
        newEvent: 'Nuovo Evento',
        settings: 'Impostazioni',
        back: 'Indietro',
        next: 'Avanti',
        save: 'Salva',
        cancel: 'Annulla',
        delete: 'Elimina',
        edit: 'Modifica',
      },
      outfit: {
        title: 'Outfit',
        day: 'Giorno',
        of: 'di',
        hair: 'Capelli',
        jewelry: 'Gioielli',
        jewels: 'Gioielli',
        purse: 'Borsa',
        bag: 'Borsa',
        shoes: 'Scarpe',
        accessories: 'Accessori',
        notes: 'Note',
        addPhotos: 'Aggiungi Foto',
        dragDrop: 'Trascina e rilascia',
        or: 'oppure',
        clickToUpload: 'clicca per caricare',
        maxPhotos: 'max {{max}} foto',
        autosave: 'Salvataggio auto',
      },
      template: {
        title: 'Template',
        select: 'Seleziona Template',
        '2piece': '2 Pezzi',
        dress: 'Abito',
        full: 'Look Completo',
        custom: 'Personalizzato',
      },
      event: {
        title: 'Eventi',
        create: 'Crea Evento',
        name: 'Nome Evento',
        startDate: 'Data Inizio',
        endDate: 'Data Fine',
        totalDays: 'Giorni Totali',
        noEvents: 'Nessun evento',
        createFirst: 'Crea il tuo primo evento',
        deleteConfirm: 'Sei sicura di voler eliminare questo evento?',
      },
      export: {
        title: 'Esporta',
        pdf: 'Esporta PDF',
        download: 'Scarica Piano',
        fashionPlan: 'PIANO MODA',
        styleTitle: 'Stile & Export',
      },
      common: {
        save: 'Salva',
        cancel: 'Annulla',
        delete: 'Elimina',
        confirm: 'Conferma',
        uploading: 'Caricamento...',
        error: 'Errore',
        photos: 'foto',
        hello: 'Ciao',
        deleteEvent: 'Elimina Evento',
        example: 'es.',
        backToEvents: 'Torna agli eventi',
        previousDay: 'Giorno precedente',
        nextDay: 'Giorno successivo',
        openStyleExport: 'Apri stile ed export',
        editDays: 'Modifica giorni',
        loadMore: 'Carica altri',
        sponsor: 'Sponsorizzato da',
        sponsorDesc: 'Gioielli siciliani artigianali',
        visitShop: 'Visita lo shop',
        sponsorTagline: 'gioielli dal cuore della Sicilia',
      },
      upload: {
        invalidType: 'Solo file JPG, PNG, WebP e HEIC sono consentiti',
        tooLarge: 'La dimensione massima è 10 MB',
        failed_one: '{{count}} file non caricato',
        failed_other: '{{count}} file non caricati',
      },
      landing: {
        heroTitle: 'Il tuo outfit perfetto, giorno per giorno',
        heroSubtitle: 'Pianifica ogni dettaglio del tuo guardaroba per Cannes — dal look completo agli accessori. Foto, note e PDF pronti da condividere.',
        cta: 'Inizia ora — è gratis',
        noCreditCard: 'Nessuna carta richiesta',
        featuresTag: 'Tutto quello che ti serve',
        featuresTitle: 'Ogni dettaglio, sotto controllo',
        feat1Title: 'Organizza per giorno',
        feat1Desc: 'Crea il tuo evento e pianifica un outfit completo per ogni giorno. Naviga facilmente tra i giorni.',
        feat2Title: 'Foto per ogni categoria',
        feat2Desc: 'Carica foto per outfit, scarpe, gioielli, borse e capelli. Tutto visivamente organizzato.',
        feat3Title: 'Template pronti',
        feat3Desc: 'Scegli tra template predefiniti: 2 Pezzi, Abito, Look Completo. Oppure personalizza le categorie.',
        feat4Title: 'Esporta in PDF',
        feat4Desc: 'Genera un PDF elegante con il tuo piano completo. Ogni giorno su una pagina separata, pronto da condividere.',
        feat5Title: 'Salvataggio automatico',
        feat5Desc: 'Ogni modifica viene salvata automaticamente nel cloud. Non perderai mai il tuo lavoro.',
        feat6Title: 'Multilingua',
        feat6Desc: 'Disponibile in Italiano, Inglese, Francese, Spagnolo e Tedesco. Perfetto per eventi internazionali.',
        howTag: 'Semplicissimo',
        howTitle: 'Come funziona',
        step1Title: 'Crea il tuo evento',
        step1Desc: 'Dai un nome al tuo evento e scegli quanti giorni dura.',
        step2Title: 'Pianifica ogni look',
        step2Desc: 'Carica foto, aggiungi note, scegli gli accessori per ogni giorno.',
        step3Title: 'Esporta e condividi',
        step3Desc: 'Scarica il PDF con tutti i tuoi outfit, pronto da stampare o inviare.',
        sponsoredBy: 'Progetto sponsorizzato da',
        ctaTitle: 'Pronta per Cannes?',
        ctaDesc: 'Crea il tuo account gratuito e inizia a pianificare il guardaroba perfetto per il tuo evento.',
        ctaButton: 'Crea il tuo account',
      },
      language: {
        title: 'Lingua',
        en: 'Inglese',
        it: 'Italiano',
        fr: 'Francese',
        es: 'Spagnolo',
        de: 'Tedesco',
      },
      errors: {
        required: 'Campo obbligatorio',
        invalidEmail: 'Email non valida',
        passwordShort: 'La password deve essere di almeno 6 caratteri',
        generic: 'Qualcosa è andato storto',
        network: 'Errore di rete',
      },
    },
  },
  fr: {
    translation: {
      app: {
        title: 'Cannes Outfit Planner',
        subtitle: 'Planifiez votre garde-robe parfaite pour l\'événement',
      },
      auth: {
        login: 'Connexion',
        register: 'S\'inscrire',
        email: 'Email',
        password: 'Mot de passe',
        name: 'Nom',
        logout: 'Déconnexion',
        noAccount: 'Pas de compte?',
        hasAccount: 'Déjà un compte?',
        registerNow: 'S\'inscrire',
        loginNow: 'Se connecter',
        welcomeBack: 'Bon retour!',
        createAccount: 'Créer votre compte',
      },
      navigation: {
        home: 'Accueil',
        myEvents: 'Mes Événements',
        newEvent: 'Nouvel Événement',
        settings: 'Paramètres',
        back: 'Retour',
        next: 'Suivant',
        save: 'Enregistrer',
        cancel: 'Annuler',
        delete: 'Supprimer',
        edit: 'Modifier',
      },
      outfit: {
        title: 'Tenue',
        day: 'Jour',
        of: 'sur',
        hair: 'Cheveux',
        jewelry: 'Bijoux',
        jewels: 'Bijoux',
        purse: 'Sac',
        bag: 'Sac',
        shoes: 'Chaussures',
        accessories: 'Accessoires',
        notes: 'Notes',
        addPhotos: 'Ajouter des Photos',
        dragDrop: 'Glisser-déposer',
        or: 'ou',
        clickToUpload: 'cliquer pour télécharger',
        maxPhotos: 'max {{max}} photos',
        autosave: 'Sauvegarde auto',
      },
      template: {
        title: 'Modèles',
        select: 'Sélectionner un Modèle',
        '2piece': '2 Pièces',
        dress: 'Robe',
        full: 'Look Complet',
        custom: 'Personnalisé',
      },
      event: {
        title: 'Événements',
        create: 'Créer un Événement',
        name: 'Nom de l\'Événement',
        startDate: 'Date de Début',
        endDate: 'Date de Fin',
        totalDays: 'Nombre de Jours',
        noEvents: 'Aucun événement',
        createFirst: 'Créez votre premier événement',
        deleteConfirm: 'Êtes-vous sûr de vouloir supprimer cet événement?',
      },
      export: {
        title: 'Exporter',
        pdf: 'Exporter PDF',
        download: 'Télécharger le Plan',
        fashionPlan: 'PLAN MODE',
        styleTitle: 'Style & Export',
      },
      common: {
        save: 'Enregistrer',
        cancel: 'Annuler',
        delete: 'Supprimer',
        confirm: 'Confirmer',
        uploading: 'Chargement...',
        error: 'Erreur',
        photos: 'photos',
        hello: 'Bonjour',
        deleteEvent: 'Supprimer l\'événement',
        example: 'ex.',
        backToEvents: 'Retour aux événements',
        previousDay: 'Jour précédent',
        nextDay: 'Jour suivant',
        openStyleExport: 'Ouvrir style et export',
        editDays: 'Modifier les jours',
        loadMore: 'Charger plus',
        sponsor: 'Sponsorisé par',
        sponsorDesc: 'Bijoux artisanaux siciliens',
        visitShop: 'Visiter la boutique',
        sponsorTagline: 'bijoux du coeur de la Sicile',
      },
      upload: {
        invalidType: 'Seuls les fichiers JPG, PNG, WebP et HEIC sont autorisés',
        tooLarge: 'La taille maximale est de 10 Mo',
        failed_one: '{{count}} fichier non téléchargé',
        failed_other: '{{count}} fichiers non téléchargés',
      },
      landing: {
        heroTitle: 'Votre tenue parfaite, jour après jour',
        heroSubtitle: 'Planifiez chaque détail de votre garde-robe pour Cannes — du look complet aux accessoires. Photos, notes et PDF prêts à partager.',
        cta: 'Commencer — c\'est gratuit',
        noCreditCard: 'Aucune carte requise',
        featuresTag: 'Tout ce dont vous avez besoin',
        featuresTitle: 'Chaque détail, sous contrôle',
        feat1Title: 'Organisez par jour',
        feat1Desc: 'Créez votre événement et planifiez une tenue complète pour chaque jour. Naviguez facilement entre les jours.',
        feat2Title: 'Photos pour chaque catégorie',
        feat2Desc: 'Téléchargez des photos pour tenues, chaussures, bijoux, sacs et cheveux. Tout visuellement organisé.',
        feat3Title: 'Modèles prêts',
        feat3Desc: 'Choisissez parmi des modèles prédéfinis : 2 Pièces, Robe, Look Complet. Ou personnalisez les catégories.',
        feat4Title: 'Exporter en PDF',
        feat4Desc: 'Générez un PDF élégant avec votre plan complet. Chaque jour sur une page séparée, prêt à partager.',
        feat5Title: 'Sauvegarde automatique',
        feat5Desc: 'Chaque modification est automatiquement sauvegardée dans le cloud. Vous ne perdrez jamais votre travail.',
        feat6Title: 'Multilingue',
        feat6Desc: 'Disponible en Anglais, Italien, Français, Espagnol et Allemand. Parfait pour les événements internationaux.',
        howTag: 'Très simple',
        howTitle: 'Comment ça marche',
        step1Title: 'Créez votre événement',
        step1Desc: 'Donnez un nom à votre événement et choisissez sa durée.',
        step2Title: 'Planifiez chaque look',
        step2Desc: 'Téléchargez des photos, ajoutez des notes, choisissez les accessoires pour chaque jour.',
        step3Title: 'Exportez et partagez',
        step3Desc: 'Téléchargez le PDF avec toutes vos tenues, prêt à imprimer ou envoyer.',
        sponsoredBy: 'Projet sponsorisé par',
        ctaTitle: 'Prête pour Cannes ?',
        ctaDesc: 'Créez votre compte gratuit et commencez à planifier la garde-robe parfaite pour votre événement.',
        ctaButton: 'Créer votre compte',
      },
      language: {
        title: 'Langue',
        en: 'Anglais',
        it: 'Italien',
        fr: 'Français',
        es: 'Espagnol',
        de: 'Allemand',
      },
      errors: {
        required: 'Champ obligatoire',
        invalidEmail: 'Email invalide',
        passwordShort: 'Le mot de passe doit contenir au moins 6 caractères',
        generic: 'Une erreur s\'est produite',
        network: 'Erreur réseau',
      },
    },
  },
  es: {
    translation: {
      app: {
        title: 'Cannes Outfit Planner',
        subtitle: 'Planifica tu armario perfecto para el evento',
      },
      auth: {
        login: 'Iniciar Sesión',
        register: 'Registrarse',
        email: 'Email',
        password: 'Contraseña',
        name: 'Nombre',
        logout: 'Cerrar Sesión',
        noAccount: '¿No tienes cuenta?',
        hasAccount: '¿Ya tienes cuenta?',
        registerNow: 'Regístrate',
        loginNow: 'Inicia sesión',
        welcomeBack: '¡Bienvenida de vuelta!',
        createAccount: 'Crea tu cuenta',
      },
      navigation: {
        home: 'Inicio',
        myEvents: 'Mis Eventos',
        newEvent: 'Nuevo Evento',
        settings: 'Configuración',
        back: 'Atrás',
        next: 'Siguiente',
        save: 'Guardar',
        cancel: 'Cancelar',
        delete: 'Eliminar',
        edit: 'Editar',
      },
      outfit: {
        title: 'Outfit',
        day: 'Día',
        of: 'de',
        hair: 'Pelo',
        jewelry: 'Joyería',
        jewels: 'Joyas',
        purse: 'Bolso',
        bag: 'Bolso',
        shoes: 'Zapatos',
        accessories: 'Accesorios',
        notes: 'Notas',
        addPhotos: 'Añadir Fotos',
        dragDrop: 'Arrastrar y soltar',
        or: 'o',
        clickToUpload: 'haz clic para subir',
        maxPhotos: 'máx {{max}} fotos',
        autosave: 'Guardado auto',
      },
      template: {
        title: 'Plantillas',
        select: 'Seleccionar Plantilla',
        '2piece': '2 Piezas',
        dress: 'Vestido',
        full: 'Look Completo',
        custom: 'Personalizado',
      },
      event: {
        title: 'Eventos',
        create: 'Crear Evento',
        name: 'Nombre del Evento',
        startDate: 'Fecha de Inicio',
        endDate: 'Fecha de Fin',
        totalDays: 'Días Totales',
        noEvents: 'Sin eventos',
        createFirst: 'Crea tu primer evento',
        deleteConfirm: '¿Estás segura de que quieres eliminar este evento?',
      },
      export: {
        title: 'Exportar',
        pdf: 'Exportar PDF',
        download: 'Descargar Plan',
        fashionPlan: 'PLAN DE MODA',
        styleTitle: 'Estilo & Exportar',
      },
      common: {
        save: 'Guardar',
        cancel: 'Cancelar',
        delete: 'Eliminar',
        confirm: 'Confirmar',
        uploading: 'Subiendo...',
        error: 'Error',
        photos: 'fotos',
        hello: 'Hola',
        deleteEvent: 'Eliminar Evento',
        example: 'ej.',
        backToEvents: 'Volver a eventos',
        previousDay: 'Día anterior',
        nextDay: 'Día siguiente',
        openStyleExport: 'Abrir estilo y exportar',
        editDays: 'Editar días',
        loadMore: 'Cargar más',
        sponsor: 'Patrocinado por',
        sponsorDesc: 'Joyería artesanal siciliana',
        visitShop: 'Visitar la tienda',
        sponsorTagline: 'joyería del corazón de Sicilia',
      },
      upload: {
        invalidType: 'Solo se permiten archivos JPG, PNG, WebP y HEIC',
        tooLarge: 'El tamaño máximo es 10 MB',
        failed_one: '{{count}} archivo no se pudo subir',
        failed_other: '{{count}} archivos no se pudieron subir',
      },
      landing: {
        heroTitle: 'Tu outfit perfecto, día a día',
        heroSubtitle: 'Planifica cada detalle de tu vestuario para Cannes — desde el look completo hasta los accesorios. Fotos, notas y PDFs listos para compartir.',
        cta: 'Empieza ya — es gratis',
        noCreditCard: 'No se requiere tarjeta',
        featuresTag: 'Todo lo que necesitas',
        featuresTitle: 'Cada detalle, bajo control',
        feat1Title: 'Organiza por día',
        feat1Desc: 'Crea tu evento y planifica un outfit completo para cada día. Navega fácilmente entre los días.',
        feat2Title: 'Fotos para cada categoría',
        feat2Desc: 'Sube fotos para outfits, zapatos, joyería, bolsos y pelo. Todo visualmente organizado.',
        feat3Title: 'Plantillas listas',
        feat3Desc: 'Elige entre plantillas predefinidas: 2 Piezas, Vestido, Look Completo. O personaliza las categorías.',
        feat4Title: 'Exportar a PDF',
        feat4Desc: 'Genera un PDF elegante con tu plan completo. Cada día en una página separada, listo para compartir.',
        feat5Title: 'Guardado automático',
        feat5Desc: 'Cada cambio se guarda automáticamente en la nube. Nunca perderás tu trabajo.',
        feat6Title: 'Multilingüe',
        feat6Desc: 'Disponible en Inglés, Italiano, Francés, Español y Alemán. Perfecto para eventos internacionales.',
        howTag: 'Muy sencillo',
        howTitle: 'Cómo funciona',
        step1Title: 'Crea tu evento',
        step1Desc: 'Dale un nombre a tu evento y elige cuántos días dura.',
        step2Title: 'Planifica cada look',
        step2Desc: 'Sube fotos, añade notas, elige los accesorios para cada día.',
        step3Title: 'Exporta y comparte',
        step3Desc: 'Descarga el PDF con todos tus outfits, listo para imprimir o enviar.',
        sponsoredBy: 'Proyecto patrocinado por',
        ctaTitle: '¿Lista para Cannes?',
        ctaDesc: 'Crea tu cuenta gratuita y empieza a planificar el vestuario perfecto para tu evento.',
        ctaButton: 'Crea tu cuenta',
      },
      language: {
        title: 'Idioma',
        en: 'Inglés',
        it: 'Italiano',
        fr: 'Francés',
        es: 'Español',
        de: 'Alemán',
      },
      errors: {
        required: 'Campo obligatorio',
        invalidEmail: 'Email inválido',
        passwordShort: 'La contraseña debe tener al menos 6 caracteres',
        generic: 'Algo salió mal',
        network: 'Error de red',
      },
    },
  },
  de: {
    translation: {
      app: {
        title: 'Cannes Outfit Planner',
        subtitle: 'Planen Sie Ihre perfekte Event-Garderobe',
      },
      auth: {
        login: 'Anmelden',
        register: 'Registrieren',
        email: 'Email',
        password: 'Passwort',
        name: 'Name',
        logout: 'Abmelden',
        noAccount: 'Kein Konto?',
        hasAccount: 'Bereits ein Konto?',
        registerNow: 'Jetzt registrieren',
        loginNow: 'Jetzt anmelden',
        welcomeBack: 'Willkommen zurück!',
        createAccount: 'Konto erstellen',
      },
      navigation: {
        home: 'Startseite',
        myEvents: 'Meine Events',
        newEvent: 'Neues Event',
        settings: 'Einstellungen',
        back: 'Zurück',
        next: 'Weiter',
        save: 'Speichern',
        cancel: 'Abbrechen',
        delete: 'Löschen',
        edit: 'Bearbeiten',
      },
      outfit: {
        title: 'Outfit',
        day: 'Tag',
        of: 'von',
        hair: 'Haare',
        jewelry: 'Schmuck',
        jewels: 'Juwelen',
        purse: 'Tasche',
        bag: 'Tasche',
        shoes: 'Schuhe',
        accessories: 'Accessoires',
        notes: 'Notizen',
        addPhotos: 'Fotos hinzufügen',
        dragDrop: 'Drag & Drop',
        or: 'oder',
        clickToUpload: 'klicken zum Hochladen',
        maxPhotos: 'max {{max}} Fotos',
        autosave: 'Autospeichern',
      },
      template: {
        title: 'Vorlagen',
        select: 'Vorlage auswählen',
        '2piece': '2-teilig',
        dress: 'Kleid',
        full: 'Kompletter Look',
        custom: 'Benutzerdefiniert',
      },
      event: {
        title: 'Events',
        create: 'Event erstellen',
        name: 'Event-Name',
        startDate: 'Startdatum',
        endDate: 'Enddatum',
        totalDays: 'Gesamttage',
        noEvents: 'Keine Events',
        createFirst: 'Erstellen Sie Ihr erstes Event',
        deleteConfirm: 'Sind Sie sicher, dass Sie dieses Event löschen möchten?',
      },
      export: {
        title: 'Exportieren',
        pdf: 'PDF exportieren',
        download: 'Plan herunterladen',
        fashionPlan: 'MODEPLAN',
        styleTitle: 'Stil & Export',
      },
      common: {
        save: 'Speichern',
        cancel: 'Abbrechen',
        delete: 'Löschen',
        confirm: 'Bestätigen',
        uploading: 'Hochladen...',
        error: 'Fehler',
        photos: 'Fotos',
        hello: 'Hallo',
        deleteEvent: 'Event löschen',
        example: 'z.B.',
        backToEvents: 'Zurück zu Events',
        previousDay: 'Vorheriger Tag',
        nextDay: 'Nächster Tag',
        openStyleExport: 'Stil und Export öffnen',
        editDays: 'Tage bearbeiten',
        loadMore: 'Mehr laden',
        sponsor: 'Gesponsert von',
        sponsorDesc: 'Sizilianischer Handwerksschmuck',
        visitShop: 'Shop besuchen',
        sponsorTagline: 'Schmuck aus dem Herzen Siziliens',
      },
      upload: {
        invalidType: 'Nur JPG, PNG, WebP und HEIC Dateien erlaubt',
        tooLarge: 'Maximale Dateigröße ist 10 MB',
        failed_one: '{{count}} Datei konnte nicht hochgeladen werden',
        failed_other: '{{count}} Dateien konnten nicht hochgeladen werden',
      },
      landing: {
        heroTitle: 'Dein perfektes Outfit, Tag für Tag',
        heroSubtitle: 'Plane jedes Detail deiner Garderobe für Cannes — vom kompletten Look bis zu den Accessoires. Fotos, Notizen und PDFs zum Teilen.',
        cta: 'Jetzt starten — kostenlos',
        noCreditCard: 'Keine Kreditkarte erforderlich',
        featuresTag: 'Alles was du brauchst',
        featuresTitle: 'Jedes Detail, unter Kontrolle',
        feat1Title: 'Nach Tagen organisieren',
        feat1Desc: 'Erstelle dein Event und plane ein komplettes Outfit für jeden Tag. Navigiere einfach zwischen den Tagen.',
        feat2Title: 'Fotos für jede Kategorie',
        feat2Desc: 'Lade Fotos hoch für Outfits, Schuhe, Schmuck, Taschen und Haare. Alles visuell organisiert.',
        feat3Title: 'Fertige Vorlagen',
        feat3Desc: 'Wähle aus Vorlagen: 2-teilig, Kleid, Kompletter Look. Oder passe die Kategorien an.',
        feat4Title: 'Als PDF exportieren',
        feat4Desc: 'Erstelle ein elegantes PDF mit deinem kompletten Plan. Jeder Tag auf einer separaten Seite, bereit zum Teilen.',
        feat5Title: 'Automatisches Speichern',
        feat5Desc: 'Jede Änderung wird automatisch in der Cloud gespeichert. Du wirst nie deine Arbeit verlieren.',
        feat6Title: 'Mehrsprachig',
        feat6Desc: 'Verfügbar auf Englisch, Italienisch, Französisch, Spanisch und Deutsch. Perfekt für internationale Events.',
        howTag: 'Ganz einfach',
        howTitle: 'So funktioniert es',
        step1Title: 'Erstelle dein Event',
        step1Desc: 'Gib deinem Event einen Namen und wähle die Anzahl der Tage.',
        step2Title: 'Plane jeden Look',
        step2Desc: 'Lade Fotos hoch, füge Notizen hinzu, wähle Accessoires für jeden Tag.',
        step3Title: 'Exportiere und teile',
        step3Desc: 'Lade das PDF mit allen Outfits herunter, bereit zum Drucken oder Versenden.',
        sponsoredBy: 'Projekt gesponsert von',
        ctaTitle: 'Bereit für Cannes?',
        ctaDesc: 'Erstelle dein kostenloses Konto und beginne, die perfekte Garderobe für dein Event zu planen.',
        ctaButton: 'Konto erstellen',
      },
      language: {
        title: 'Sprache',
        en: 'Englisch',
        it: 'Italienisch',
        fr: 'Französisch',
        es: 'Spanisch',
        de: 'Deutsch',
      },
      errors: {
        required: 'Pflichtfeld',
        invalidEmail: 'Ungültige Email',
        passwordShort: 'Passwort muss mindestens 6 Zeichen haben',
        generic: 'Etwas ist schief gelaufen',
        network: 'Netzwerkfehler',
      },
    },
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    debug: false,
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
  });

export default i18n;
